import { compareAsc } from 'date-fns';
import { isNil } from 'lodash';

import type { SingleSortInfo, SortBy, SortConfig, SortInfo } from './types';

// ----------------------------------------------------------------------

const sortDataSource = <TSource extends object, TExtractedData extends {}>(
  sourceData: TSource[],
  extractItem: (sourceItem: TSource) => TExtractedData | null | undefined,
  compare: (a: NonNullable<TExtractedData>, b: NonNullable<TExtractedData>) => number,
): TSource[] => {
  if (sourceData.length === 0) {
    return sourceData;
  }

  return [...sourceData].sort((a, b) => {
    const sortableItemA = extractItem(a);
    const sortableItemB = extractItem(b);

    if (isNil(sortableItemA) || isNil(sortableItemB)) {
      return 0;
    }

    return compare(sortableItemA, sortableItemB);
  });
};

// ----------------------------------------------------------------------

const sortByString = <TSource extends object>(
  sourceData: TSource[],
  extractItem: (sourceItem: TSource) => string | null | undefined,
  sortInfo: SingleSortInfo<TSource>,
): TSource[] => sortDataSource(sourceData, extractItem, (a, b) => a.localeCompare(b) * sortInfo.dir);

// ----------------------------------------------------------------------

const sortByNumber = <TSource extends object>(
  sourceData: TSource[],
  extractItem: (sourceItem: TSource) => number | null | undefined,
  sortInfo: SingleSortInfo<TSource>,
): TSource[] => sortDataSource(sourceData, extractItem, (a, b) => (a - b) * sortInfo.dir);

// ----------------------------------------------------------------------

const sortByDate = <TSource extends object>(
  sourceData: TSource[],
  extractItem: (sourceItem: TSource) => Date | null | undefined,
  sortInfo: SingleSortInfo<TSource>,
): TSource[] => sortDataSource(sourceData, extractItem, (a, b) => compareAsc(a, b) * sortInfo.dir);

// ----------------------------------------------------------------------

const sortBy: SortBy<unknown> = {
  string: extractItem => ({ type: 'string', extractItem }),
  number: extractItem => ({ type: 'number', extractItem }),
  date: extractItem => ({ type: 'date', extractItem }),
};

// ----------------------------------------------------------------------

type CreateSortFunctionOptions = {
  isSortWithChildren?: boolean;
};

type CreateSortFunction = <TSource extends object>(
  config: SortConfig<TSource>,
  options?: CreateSortFunctionOptions,
) => (sourceData: TSource[], sortInfo: SortInfo<TSource> | null) => TSource[];

export const createSortFunction: CreateSortFunction = <TSource extends object>(
  config: SortConfig<TSource>,
  options?: CreateSortFunctionOptions,
) => {
  const sort = (sourceData: TSource[], sortInfo: SingleSortInfo<TSource>) => {
    const { name } = sortInfo;
    const getSortConfig = config[name];

    if (!getSortConfig) {
      return sourceData;
    }

    const { type, extractItem } = getSortConfig(sortBy);

    if (type === 'string') {
      return sortByString(sourceData, sourceItem => extractItem(sourceItem[name]), sortInfo);
    }
    if (type === 'number') {
      return sortByNumber(sourceData, sourceItem => extractItem(sourceItem[name]), sortInfo);
    }
    if (type === 'date') {
      return sortByDate(sourceData, sourceItem => extractItem(sourceItem[name]), sortInfo);
    }

    throw new Error(`Unknown sort type: ${type}`);
  };

  return (sourceData: TSource[], sortInfo: SortInfo<TSource> | null) => {
    if (!sortInfo) {
      return sourceData;
    }

    if (Array.isArray(sortInfo)) {
      return sortInfo.reduce((sortedData, sortInfoItem) => sort(sortedData, sortInfoItem), sourceData);
    }

    const sortedDataSource = sort(sourceData, sortInfo);

    if (options?.isSortWithChildren) {
      return sortedDataSource.map(sourceItem => {
        if ('nodes' in sourceItem && Array.isArray(sourceItem.nodes)) {
          return {
            ...sourceItem,
            nodes: sort(sourceItem.nodes, sortInfo),
          };
        }
        return sourceItem;
      });
    }

    return sortedDataSource;
  };
};
